/**
 * Contest Entry Screen - Manager View
 * 
 * Allows managers to view, search, filter, and rate all contest entries.
 * 
 * @module ContestEntryScreen
 */

import React, { useState, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import {
  Search,
  Filter,
  Star,
  Calendar,
  Store,
  User,
  MapPin,
  Trophy,
  CheckCircle,
  Clock,
  XCircle,
  Eye,
  X,
  Check,
} from 'lucide-react';
import { ThemeColors, Spacing, Typography, BorderRadius, Shadows } from '../../styles/ThemeStyles';
import { PageContainer, MainContent } from '../../styles/StyledComponents';
import { ContestEntryService } from '../../services/ContestEntryService';
import {
  ContestEntry,
  ContestEntryFilters,
  ContestEntrySort,
  ContestEntryStatus,
  RateContestEntryData,
} from '../../@types/contestEntry';
import toast from 'react-hot-toast';

const Header = styled.div`
  margin-bottom: ${Spacing.xl};
`;

const Title = styled.h1`
  font-size: ${Typography.fontSize.xxl};
  font-weight: ${Typography.fontWeight.bold};
  color: ${ThemeColors.text.primary};
  margin-bottom: ${Spacing.sm};
  display: flex;
  align-items: center;
  gap: ${Spacing.md};
`;

const Subtitle = styled.p`
  font-size: ${Typography.fontSize.md};
  color: ${ThemeColors.text.secondary};
`;

const ToggleBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${Spacing.md};
  padding: ${Spacing.md} ${Spacing.lg};
  background: ${ThemeColors.gray[50]};
  border: 1px solid ${ThemeColors.border.light};
  border-radius: ${BorderRadius.md};
  margin-bottom: ${Spacing.xl};
`;

const ToggleLabel = styled.span`
  font-size: ${Typography.fontSize.md};
  font-weight: ${Typography.fontWeight.medium};
  color: ${ThemeColors.text.primary};
`;

const ToggleSwitch = styled.button<{ $enabled: boolean }>`
  position: relative;
  width: 52px;
  height: 28px;
  border-radius: 14px;
  border: 2px solid ${props => props.$enabled ? ThemeColors.primary.main : ThemeColors.border.medium};
  background: ${props => props.$enabled ? ThemeColors.primary.main : ThemeColors.gray[200]};
  cursor: pointer;
  transition: all 0.2s ease;

  &::after {
    content: '';
    position: absolute;
    top: 2px;
    left: ${props => props.$enabled ? '26px' : '2px'};
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background: white;
    box-shadow: ${Shadows.sm};
    transition: left 0.2s ease;
  }

  &:hover {
    border-color: ${props => props.$enabled ? ThemeColors.primary.dark : ThemeColors.text.secondary};
  }
`;

const ToggleStatus = styled.span<{ $enabled: boolean }>`
  font-size: ${Typography.fontSize.sm};
  font-weight: ${Typography.fontWeight.semiBold};
  color: ${props => props.$enabled ? ThemeColors.success.main : ThemeColors.text.secondary};
`;

const SearchFilterBar = styled.div`
  display: flex;
  gap: ${Spacing.md};
  margin-bottom: ${Spacing.xl};
  flex-wrap: wrap;
  align-items: center;
`;

const SearchContainer = styled.div`
  position: relative;
  flex: 1;
  min-width: 300px;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: ${Spacing.md} ${Spacing.md} ${Spacing.md} 40px;
  border: 2px solid ${ThemeColors.border.main};
  border-radius: ${BorderRadius.md};
  font-size: ${Typography.fontSize.md};
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: ${ThemeColors.primary.main};
    box-shadow: 0 0 0 3px ${ThemeColors.primary.light};
  }
`;

const SearchIcon = styled(Search)`
  position: absolute;
  left: ${Spacing.md};
  top: 50%;
  transform: translateY(-50%);
  color: ${ThemeColors.text.secondary};
  width: 20px;
  height: 20px;
`;

const FilterButton = styled.button<{ active?: boolean }>`
  display: flex;
  align-items: center;
  gap: ${Spacing.sm};
  padding: ${Spacing.md} ${Spacing.lg};
  background: ${props => props.active ? ThemeColors.primary.main : 'white'};
  color: ${props => props.active ? 'white' : ThemeColors.text.primary};
  border: 2px solid ${props => props.active ? ThemeColors.primary.main : ThemeColors.border.main};
  border-radius: ${BorderRadius.md};
  font-size: ${Typography.fontSize.sm};
  font-weight: ${Typography.fontWeight.medium};
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: ${props => props.active ? ThemeColors.primary.dark : ThemeColors.background.secondary};
    border-color: ${props => props.active ? ThemeColors.primary.dark : ThemeColors.primary.main};
  }
`;

const FilterPanel = styled.div<{ isOpen: boolean }>`
  display: ${props => props.isOpen ? 'block' : 'none'};
  background: white;
  border-radius: ${BorderRadius.lg};
  padding: ${Spacing.lg};
  margin-bottom: ${Spacing.xl};
  box-shadow: ${Shadows.md};
  border: 1px solid ${ThemeColors.border.light};
`;

const FilterGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: ${Spacing.md};
`;

const FilterGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Spacing.xs};
`;

const FilterLabel = styled.label`
  font-size: ${Typography.fontSize.sm};
  font-weight: ${Typography.fontWeight.semiBold};
  color: ${ThemeColors.text.primary};
`;

const Select = styled.select`
  padding: ${Spacing.sm} ${Spacing.md};
  border: 2px solid ${ThemeColors.border.main};
  border-radius: ${BorderRadius.md};
  font-size: ${Typography.fontSize.sm};
  background: white;
  cursor: pointer;
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: ${ThemeColors.primary.main};
  }
`;

const EntriesContainer = styled.div`
  margin-bottom: ${Spacing.xl};
  overflow-x: auto;
`;

const EntriesTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  background: white;
  border-radius: ${BorderRadius.lg};
  overflow: hidden;
  box-shadow: ${Shadows.sm};
  
  @media (max-width: 1024px) {
    display: none;
  }
`;

const TableHeader = styled.thead`
  background: ${ThemeColors.gray[50]};
`;

const TableHeaderRow = styled.tr`
  border-bottom: 2px solid ${ThemeColors.border.light};
`;

const TableHeaderCell = styled.th`
  padding: ${Spacing.md};
  text-align: left;
  font-size: ${Typography.fontSize.sm};
  font-weight: ${Typography.fontWeight.semiBold};
  color: ${ThemeColors.text.secondary};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const TableBody = styled.tbody``;

const TableRow = styled.tr<{ rating?: number }>`
  border-bottom: 1px solid ${ThemeColors.border.light};
  transition: all 0.2s ease;
  cursor: pointer;
  
  &:hover {
    background: ${ThemeColors.gray[50]};
  }
  
  ${props => props.rating && `
    background: ${ThemeColors.primary.light}15;
  `}
`;

const TableCell = styled.td`
  padding: ${Spacing.md};
  font-size: ${Typography.fontSize.sm};
  color: ${ThemeColors.text.primary};
  vertical-align: middle;
`;

const TableImage = styled.img`
  width: 80px;
  height: 80px;
  object-fit: cover;
  border-radius: ${BorderRadius.md};
  cursor: pointer;
`;

const TableActions = styled.div`
  display: flex;
  gap: ${Spacing.xs};
  align-items: center;
`;

const EntriesGrid = styled.div`
  display: none;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: ${Spacing.lg};
  margin-bottom: ${Spacing.xl};
  
  @media (max-width: 1024px) {
    display: grid;
  }
`;

const EntryCard = styled.div<{ rating?: number }>`
  background: white;
  border-radius: ${BorderRadius.lg};
  overflow: hidden;
  box-shadow: ${Shadows.sm};
  border: 2px solid ${props => props.rating ? ThemeColors.primary.light : ThemeColors.border.light};
  transition: all 0.2s ease;

  &:hover {
    box-shadow: ${Shadows.md};
    transform: translateY(-2px);
  }
`;

const EntryImage = styled.img`
  width: 100%;
  height: 250px;
  object-fit: cover;
  background: ${ThemeColors.background.secondary};
`;

const EntryContent = styled.div`
  padding: ${Spacing.lg};
`;

const EntryHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: ${Spacing.md};
`;

const EntryTitle = styled.h3`
  font-size: ${Typography.fontSize.lg};
  font-weight: ${Typography.fontWeight.semiBold};
  color: ${ThemeColors.text.primary};
  margin: 0 0 ${Spacing.xs} 0;
  display: flex;
  align-items: center;
  gap: ${Spacing.xs};
`;

const StatusBadge = styled.span<{ status: ContestEntryStatus }>`
  display: inline-flex;
  align-items: center;
  gap: ${Spacing.xs};
  padding: ${Spacing.xs} ${Spacing.sm};
  border-radius: ${BorderRadius.full};
  font-size: ${Typography.fontSize.xs};
  font-weight: ${Typography.fontWeight.medium};
  text-transform: uppercase;

  ${props => {
    switch (props.status) {
      case 'pending':
        return `
          background: ${ThemeColors.warning.light};
          color: ${ThemeColors.warning.dark};
        `;
      case 'reviewed':
        return `
          background: ${ThemeColors.info.light};
          color: ${ThemeColors.info.dark};
        `;
      case 'approved':
        return `
          background: ${ThemeColors.success.light};
          color: ${ThemeColors.success.dark};
        `;
      case 'rejected':
        return `
          background: ${ThemeColors.error.light};
          color: ${ThemeColors.error.dark};
        `;
      default:
        return `
          background: ${ThemeColors.gray[100]};
          color: ${ThemeColors.gray[600]};
        `;
    }
  }}
`;

const EntryInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Spacing.xs};
  margin-bottom: ${Spacing.md};
  font-size: ${Typography.fontSize.sm};
  color: ${ThemeColors.text.secondary};
`;

const InfoRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${Spacing.xs};
`;

const EntryMessage = styled.p`
  font-size: ${Typography.fontSize.sm};
  color: ${ThemeColors.text.primary};
  margin: ${Spacing.md} 0;
  padding: ${Spacing.md};
  background: ${ThemeColors.background.secondary};
  border-radius: ${BorderRadius.md};
  font-style: italic;
`;

const RatingSection = styled.div`
  border-top: 1px solid ${ThemeColors.border.light};
  padding-top: ${Spacing.md};
  margin-top: ${Spacing.md};
`;

const RatingDisplay = styled.div`
  display: flex;
  align-items: center;
  gap: ${Spacing.sm};
  margin-bottom: ${Spacing.sm};
`;

const StarsContainer = styled.div`
  display: flex;
  gap: ${Spacing.xs};
`;

const StarIcon = styled(Star)<{ $filled?: boolean }>`
  width: 20px;
  height: 20px;
  color: ${props => props.$filled ? ThemeColors.warning.main : ThemeColors.border.main};
  fill: ${props => props.$filled ? ThemeColors.warning.main : 'none'};
`;

const RatingInput = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Spacing.sm};
`;

const RatingStars = styled.div`
  display: flex;
  gap: ${Spacing.xs};
  cursor: pointer;
`;

const RatingStar = styled(Star)<{ $filled?: boolean; $hovered?: boolean }>`
  width: 24px;
  height: 24px;
  color: ${props => props.$filled || props.$hovered ? ThemeColors.warning.main : ThemeColors.border.main};
  fill: ${props => props.$filled || props.$hovered ? ThemeColors.warning.main : 'none'};
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    transform: scale(1.1);
  }
`;

const RatingNote = styled.textarea`
  width: 100%;
  padding: ${Spacing.sm};
  border: 2px solid ${ThemeColors.border.main};
  border-radius: ${BorderRadius.md};
  font-size: ${Typography.fontSize.sm};
  font-family: inherit;
  resize: vertical;
  min-height: 60px;

  &:focus {
    outline: none;
    border-color: ${ThemeColors.primary.main};
  }
`;

const ActionButtons = styled.div`
  display: flex;
  gap: ${Spacing.sm};
  margin-top: ${Spacing.md};
`;

const Button = styled.button<{ variant?: 'primary' | 'secondary' | 'success' | 'danger' }>`
  flex: 1;
  padding: ${Spacing.sm} ${Spacing.md};
  border: none;
  border-radius: ${BorderRadius.md};
  font-size: ${Typography.fontSize.sm};
  font-weight: ${Typography.fontWeight.medium};
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${Spacing.xs};

  ${props => {
    switch (props.variant) {
      case 'primary':
        return `
          background: ${ThemeColors.primary.main};
          color: white;
          &:hover {
            background: ${ThemeColors.primary.dark};
          }
        `;
      case 'success':
        return `
          background: ${ThemeColors.success.main};
          color: white;
          &:hover {
            background: ${ThemeColors.success.dark};
          }
        `;
      case 'danger':
        return `
          background: ${ThemeColors.error.main};
          color: white;
          &:hover {
            background: ${ThemeColors.error.dark};
          }
        `;
      default:
        return `
          background: ${ThemeColors.background.secondary};
          color: ${ThemeColors.text.primary};
          &:hover {
            background: ${ThemeColors.gray[100]};
          }
        `;
    }
  }}

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: ${Spacing.xxl};
  color: ${ThemeColors.text.secondary};
`;

const Modal = styled.div<{ isOpen: boolean }>`
  display: ${props => props.isOpen ? 'flex' : 'none'};
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: ${Spacing.lg};
`;

const ModalContent = styled.div`
  background: white;
  border-radius: ${BorderRadius.xl};
  max-width: 900px;
  max-height: 90vh;
  overflow-y: auto;
  width: 100%;
  position: relative;
`;

const ModalImage = styled.img`
  width: 100%;
  max-height: 60vh;
  object-fit: contain;
  background: ${ThemeColors.background.secondary};
`;

const ModalHeader = styled.div`
  padding: ${Spacing.lg};
  border-bottom: 1px solid ${ThemeColors.border.light};
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: sticky;
  top: 0;
  background: white;
  z-index: 1;
`;

const ModalClose = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: ${Spacing.sm};
  color: ${ThemeColors.text.secondary};
  border-radius: ${BorderRadius.md};

  &:hover {
    background: ${ThemeColors.background.secondary};
  }
`;

const formatDate = (date: Date | any): string => {
  if (!date) return 'N/A';
  const d = date.toDate ? date.toDate() : new Date(date);
  return d.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const getStatusIcon = (status: ContestEntryStatus) => {
  switch (status) {
    case 'pending':
      return <Clock size={14} />;
    case 'reviewed':
      return <Eye size={14} />;
    case 'approved':
      return <CheckCircle size={14} />;
    case 'rejected':
      return <XCircle size={14} />;
    default:
      return <Clock size={14} />;
  }
};

const MOBILE_BREAKPOINT = 1024;

const getIsMobile = () => {
  if (typeof window === 'undefined') return false;
  const widthMatch = window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT}px)`).matches;
  const coarsePointer = window.matchMedia('(pointer: coarse)').matches;
  return widthMatch || coarsePointer;
};

export const ContestEntryScreen: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [entries, setEntries] = useState<ContestEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [isMobile, setIsMobile] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState<ContestEntryFilters>({});
  const [showFilters, setShowFilters] = useState(false);
  const [sort, setSort] = useState<ContestEntrySort>({ field: 'submittedAt', direction: 'desc' });
  const [selectedEntry, setSelectedEntry] = useState<ContestEntry | null>(null);
  const [rating, setRating] = useState(0);
  const [hoveredRating, setHoveredRating] = useState(0);
  const [ratingNote, setRatingNote] = useState('');
  const [ratingEntry, setRatingEntry] = useState<ContestEntry | null>(null);
  const [contestEnabled, setContestEnabled] = useState(true);
  const [loadingContestSettings, setLoadingContestSettings] = useState(true);
  const [updatingContestSettings, setUpdatingContestSettings] = useState(false);

  // Mobile detection
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const handleChange = () => setIsMobile(getIsMobile());
    handleChange();

    const widthMedia = window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT}px)`);
    const pointerMedia = window.matchMedia('(pointer: coarse)');

    if (widthMedia.addEventListener) {
      widthMedia.addEventListener('change', handleChange);
      pointerMedia.addEventListener('change', handleChange);
    } else {
      widthMedia.addListener(handleChange);
      pointerMedia.addListener(handleChange);
    }

    window.addEventListener('orientationchange', handleChange);
    window.addEventListener('resize', handleChange);

    return () => {
      if (widthMedia.removeEventListener) {
        widthMedia.removeEventListener('change', handleChange);
        pointerMedia.removeEventListener('change', handleChange);
      } else {
        widthMedia.removeListener(handleChange);
        pointerMedia.removeListener(handleChange);
      }
      window.removeEventListener('orientationchange', handleChange);
      window.removeEventListener('resize', handleChange);
    };
  }, []);

  useEffect(() => {
    loadEntries();
  }, [user, filters, sort]);

  useEffect(() => {
    const loadContestSettings = async () => {
      try {
        setLoadingContestSettings(true);
        const enabled = await ContestEntryService.getContestEnabled();
        setContestEnabled(enabled);
      } catch (error) {
        console.error('Error loading contest settings:', error);
      } finally {
        setLoadingContestSettings(false);
      }
    };
    loadContestSettings();
  }, []);

  const handleContestToggle = async () => {
    if (updatingContestSettings) return;
    const newEnabled = !contestEnabled;
    try {
      setUpdatingContestSettings(true);
      await ContestEntryService.setContestEnabled(newEnabled);
      setContestEnabled(newEnabled);
      toast.success(
        newEnabled
          ? t('contestEntry.contestEnabled', 'Contest is now open for submissions')
          : t('contestEntry.contestDisabled', 'Contest is now closed for submissions')
      );
    } catch (error: any) {
      toast.error(t('contestEntry.toggleError', 'Failed to update contest settings'));
    } finally {
      setUpdatingContestSettings(false);
    }
  };

  const loadEntries = async () => {
    if (!user) return;

    try {
      setLoading(true);
      const userRole = user.role === 'manager' ? 'manager' : 'sales-representative';
      const allEntries = await ContestEntryService.getAllEntries(
        user.id,
        userRole,
        filters,
        sort
      );
      setEntries(allEntries);
    } catch (error: any) {
      console.error('Error loading entries:', error);
      toast.error('Failed to load contest entries');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (value: string) => {
    setSearchTerm(value);
    setFilters(prev => ({
      ...prev,
      searchTerm: value || undefined,
    }));
  };

  const handleFilterChange = (key: keyof ContestEntryFilters, value: any) => {
    setFilters(prev => ({
      ...prev,
      [key]: value || undefined,
    }));
  };

  const handleRating = async (entry: ContestEntry, newRating: number) => {
    if (!user || user.role !== 'manager') return;

    try {
      const ratingData: RateContestEntryData = {
        rating: newRating,
        notes: ratingNote.trim() || undefined,
        status: 'reviewed',
      };

      await ContestEntryService.rateEntry(
        entry.id,
        ratingData,
        user.id,
        user.username || user.email || 'Manager'
      );

      const managerRating = ContestEntryService.getManagerRating(entry, user.id);
      const actionText = managerRating ? 'updated' : 'added';
      toast.success(`Your rating ${actionText} successfully! Entry now has ${(entry.totalRatings || 0) + (managerRating ? 0 : 1)} rating(s).`);
      setRatingEntry(null);
      setRating(0);
      setRatingNote('');
      await loadEntries();
    } catch (error: any) {
      console.error('Error rating entry:', error);
      toast.error('Failed to rate entry');
    }
  };
  
  // Helper function to get manager's own rating
  const getMyRating = (entry: ContestEntry): number | undefined => {
    if (!user) return undefined;
    return ContestEntryService.getManagerRating(entry, user.id)?.rating;
  };
  
  // Helper function to check if current manager has rated
  const haveIRated = (entry: ContestEntry): boolean => {
    if (!user) return false;
    return ContestEntryService.hasManagerRated(entry, user.id);
  };
  
  // Helper to get effective rating (handles backwards compatibility)
  const getEffectiveRating = (entry: ContestEntry) => {
    return entry.averageRating || entry.rating || 0;
  };
  
  // Helper to check if entry has any rating
  const hasAnyRating = (entry: ContestEntry): boolean => {
    return !!(entry.averageRating || entry.rating);
  };

  const handleStatusChange = async (entryId: string, newStatus: ContestEntryStatus) => {
    try {
      await ContestEntryService.updateEntryStatus(entryId, newStatus);
      toast.success('Status updated successfully!');
      await loadEntries();
    } catch (error: any) {
      console.error('Error updating status:', error);
      toast.error('Failed to update status');
    }
  };

  const filteredEntries = useMemo(() => {
    if (!searchTerm) return entries;
    const term = searchTerm.toLowerCase();
    return entries.filter(entry =>
      entry.storeName?.toLowerCase().includes(term) ||
      entry.submittedByName?.toLowerCase().includes(term) ||
      entry.storeCustomerNo?.toLowerCase().includes(term) ||
      entry.message?.toLowerCase().includes(term)
    );
  }, [entries, searchTerm]);

  if (loading) {
    return (
      <PageContainer isMenuCollapsed={false}>
        <MainContent>
          <EmptyState>Loading contest entries...</EmptyState>
        </MainContent>
      </PageContainer>
    );
  }

  return (
    <PageContainer isMenuCollapsed={false}>
      <MainContent>
        <Header>
          <Title>
            <Trophy size={32} />
            Contest Entries
          </Title>
          <Subtitle>Review and rate all contest entries submitted by sales representatives</Subtitle>
        </Header>

        {!loadingContestSettings && (
          <ToggleBar>
            <ToggleLabel>{t('contestEntry.contestOpenForReps', 'Contest open for sales reps')}</ToggleLabel>
            <div style={{ display: 'flex', alignItems: 'center', gap: Spacing.md }}>
              <ToggleStatus $enabled={contestEnabled}>
                {contestEnabled ? t('contestEntry.enabled', 'ON') : t('contestEntry.disabled', 'OFF')}
              </ToggleStatus>
              <ToggleSwitch
                $enabled={contestEnabled}
                onClick={handleContestToggle}
                disabled={updatingContestSettings}
                aria-label={t('contestEntry.toggleContest', 'Toggle contest entry on or off')}
              />
            </div>
          </ToggleBar>
        )}

      <SearchFilterBar>
        <SearchContainer>
          <SearchIcon />
          <SearchInput
            type="text"
            placeholder="Search by store name, submitter, or customer number..."
            value={searchTerm}
            onChange={(e) => handleSearch(e.target.value)}
          />
        </SearchContainer>
        <FilterButton
          active={showFilters}
          onClick={() => setShowFilters(!showFilters)}
        >
          <Filter size={18} />
          Filters
        </FilterButton>
      </SearchFilterBar>

      {showFilters && (
        <FilterPanel isOpen={showFilters}>
          <FilterGrid>
            <FilterGroup>
              <FilterLabel>Status</FilterLabel>
              <Select
                value={filters.status as string || 'all'}
                onChange={(e) => handleFilterChange('status', e.target.value === 'all' ? undefined : e.target.value)}
              >
                <option value="all">All Statuses</option>
                <option value="pending">Pending</option>
                <option value="reviewed">Reviewed</option>
                <option value="approved">Approved</option>
                <option value="rejected">Rejected</option>
              </Select>
            </FilterGroup>

            <FilterGroup>
              <FilterLabel>Rating Status</FilterLabel>
              <Select
                value={filters.rated ? 'rated' : filters.rated === false ? 'unrated' : 'all'}
                onChange={(e) => handleFilterChange('rated', e.target.value === 'all' ? undefined : e.target.value === 'rated')}
              >
                <option value="all">All Entries</option>
                <option value="rated">Rated</option>
                <option value="unrated">Not Rated</option>
              </Select>
            </FilterGroup>

            <FilterGroup>
              <FilterLabel>Sort By</FilterLabel>
              <Select
                value={sort.field}
                onChange={(e) => setSort({ ...sort, field: e.target.value as ContestEntrySort['field'] })}
              >
                <option value="submittedAt">Submission Date</option>
                <option value="storeName">Store Name</option>
                <option value="submittedByName">Submitter Name</option>
                <option value="rating">Rating</option>
                <option value="status">Status</option>
              </Select>
            </FilterGroup>

            <FilterGroup>
              <FilterLabel>Sort Direction</FilterLabel>
              <Select
                value={sort.direction}
                onChange={(e) => setSort({ ...sort, direction: e.target.value as ContestEntrySort['direction'] })}
              >
                <option value="desc">Descending</option>
                <option value="asc">Ascending</option>
              </Select>
            </FilterGroup>
          </FilterGrid>
        </FilterPanel>
      )}

      {filteredEntries.length === 0 ? (
        <EmptyState>
          <Trophy size={48} style={{ marginBottom: Spacing.md, opacity: 0.5 }} />
          <p>No contest entries found</p>
        </EmptyState>
      ) : (
        <EntriesContainer>
          {/* Desktop Table View */}
          <EntriesTable>
            <TableHeader>
              <TableHeaderRow>
                <TableHeaderCell>Image</TableHeaderCell>
                <TableHeaderCell>Store</TableHeaderCell>
                <TableHeaderCell>Location</TableHeaderCell>
                <TableHeaderCell>Submitted By</TableHeaderCell>
                <TableHeaderCell>Date</TableHeaderCell>
                <TableHeaderCell>Status</TableHeaderCell>
                <TableHeaderCell>Rating</TableHeaderCell>
                <TableHeaderCell>Actions</TableHeaderCell>
              </TableHeaderRow>
            </TableHeader>
            <TableBody>
              {filteredEntries.map((entry) => (
                <TableRow key={entry.id} rating={entry.rating} onClick={() => setSelectedEntry(entry)}>
                  <TableCell>
                    <TableImage
                      src={entry.imageUrl}
                      alt={entry.storeName}
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedEntry(entry);
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <div style={{ fontWeight: Typography.fontWeight.semiBold }}>
                      {entry.storeName}
                    </div>
                    <div style={{ fontSize: Typography.fontSize.xs, color: ThemeColors.text.secondary }}>
                      #{entry.storeCustomerNo}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div style={{ display: 'flex', alignItems: 'center', gap: Spacing.xs }}>
                      <MapPin size={14} />
                      <span>{entry.storeCity}{entry.storeProvince ? `, ${entry.storeProvince}` : ''}</span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div style={{ display: 'flex', alignItems: 'center', gap: Spacing.xs }}>
                      <User size={14} />
                      {entry.submittedByName}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div style={{ display: 'flex', alignItems: 'center', gap: Spacing.xs }}>
                      <Calendar size={14} />
                      {formatDate(entry.submittedAt)}
                    </div>
                  </TableCell>
                  <TableCell>
                    <StatusBadge status={entry.status}>
                      {getStatusIcon(entry.status)}
                      {entry.status}
                    </StatusBadge>
                  </TableCell>
                  <TableCell>
                    {hasAnyRating(entry) ? (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                        {/* Show manager's own rating first if they've rated */}
                        {haveIRated(entry) && (
                          <div style={{ display: 'flex', alignItems: 'center', gap: Spacing.xs }}>
                            <span style={{ fontSize: Typography.fontSize.xs, fontWeight: 600, color: ThemeColors.primary.main }}>
                              You:
                            </span>
                            <StarsContainer style={{ transform: 'scale(0.9)' }}>
                              {[1, 2, 3, 4, 5].map((star) => (
                                <StarIcon
                                  key={star}
                                  $filled={star <= Math.round((getMyRating(entry) || 0) / 2)}
                                />
                              ))}
                            </StarsContainer>
                            <span style={{ fontSize: Typography.fontSize.xs, fontWeight: 600, color: ThemeColors.primary.main }}>
                              {getMyRating(entry)}/10
                            </span>
                          </div>
                        )}
                        
                        {/* Show overall average rating */}
                        {entry.averageRating ? (
                          <div style={{ display: 'flex', alignItems: 'center', gap: Spacing.xs }}>
                            <span style={{ fontSize: Typography.fontSize.xs, fontWeight: 600 }}>
                              Overall:
                            </span>
                 <StarsContainer style={{ transform: 'scale(0.9)' }}>
                   {[1, 2, 3, 4, 5].map((star) => (
                     <StarIcon
                       key={star}
                       $filled={star <= Math.round(getEffectiveRating(entry) / 2)}
                     />
                   ))}
                 </StarsContainer>
                            <span style={{ fontSize: Typography.fontSize.xs, fontWeight: 600 }}>
                              {entry.averageRating.toFixed(1)}/10
                            </span>
                            <span style={{ fontSize: Typography.fontSize.xs, color: ThemeColors.text.secondary }}>
                              ({entry.totalRatings} {entry.totalRatings === 1 ? 'mgr' : 'mgrs'})
                            </span>
                          </div>
                         ) : (
                           /* Legacy single rating display */
                           <div style={{ display: 'flex', alignItems: 'center', gap: Spacing.xs }}>
                            <StarsContainer>
                              {[1, 2, 3, 4, 5].map((star) => (
                                <StarIcon
                                  key={star}
                                  $filled={star <= Math.round(getEffectiveRating(entry) / 2)}
                                />
                              ))}
                            </StarsContainer>
                            <span style={{ fontSize: Typography.fontSize.xs, fontWeight: 600 }}>
                              {entry.rating}/10
                            </span>
                          </div>
                         )}
                       </div>
                    ) : (
                      <span style={{ color: ThemeColors.text.secondary, fontSize: Typography.fontSize.xs }}>Not rated</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <TableActions onClick={(e) => e.stopPropagation()}>
                      {!haveIRated(entry) ? (
                        <Button
                          variant="primary"
                          onClick={() => setRatingEntry(entry)}
                          style={{ padding: `${Spacing.xs} ${Spacing.sm}` }}
                        >
                          <Star size={14} />
                          Rate
                        </Button>
                      ) : (
                        <Button
                          variant="secondary"
                          onClick={() => {
                            const myRating = ContestEntryService.getManagerRating(entry, user?.id || '');
                            setRatingEntry(entry);
                            setRating(myRating?.rating || 0);
                            setRatingNote(myRating?.notes || '');
                          }}
                          style={{ padding: `${Spacing.xs} ${Spacing.sm}` }}
                        >
                          <Star size={14} />
                          Update
                        </Button>
                      )}
                      <Button
                        variant="secondary"
                        onClick={() => setSelectedEntry(entry)}
                        style={{ padding: `${Spacing.xs} ${Spacing.sm}` }}
                      >
                        <Eye size={14} />
                      </Button>
                    </TableActions>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </EntriesTable>

          {/* Mobile Cards View */}
          <EntriesGrid>
            {filteredEntries.map((entry) => (
              <EntryCard key={entry.id} rating={entry.rating}>
                <EntryImage
                  src={entry.imageUrl}
                  alt={entry.storeName}
                  onClick={() => setSelectedEntry(entry)}
                  style={{ cursor: 'pointer' }}
                />
                <EntryContent>
                  <EntryHeader>
                    <div>
                      <EntryTitle>
                        <Store size={18} />
                        {entry.storeName}
                      </EntryTitle>
                      <StatusBadge status={entry.status}>
                        {getStatusIcon(entry.status)}
                        {entry.status}
                      </StatusBadge>
                    </div>
                  </EntryHeader>

                  <EntryInfo>
                    <InfoRow>
                      <MapPin size={14} />
                      {entry.storeAddress}, {entry.storeCity}
                      {entry.storeProvince && `, ${entry.storeProvince}`}
                    </InfoRow>
                    <InfoRow>
                      <strong>Customer #:</strong> {entry.storeCustomerNo}
                    </InfoRow>
                    <InfoRow>
                      <User size={14} />
                      {entry.submittedByName}
                    </InfoRow>
                    <InfoRow>
                      <Calendar size={14} />
                      {formatDate(entry.submittedAt)}
                    </InfoRow>
                  </EntryInfo>

                  {entry.message && (
                    <EntryMessage>{entry.message}</EntryMessage>
                  )}

                  {hasAnyRating(entry) && (
                    <RatingSection>
                      {/* Show manager's own rating prominently if they've rated */}
                      {haveIRated(entry) && (
                        <RatingDisplay style={{ marginBottom: Spacing.sm, borderLeft: `4px solid ${ThemeColors.primary.main}`, paddingLeft: Spacing.sm }}>
                          <strong style={{ color: ThemeColors.primary.main }}>Your Rating:</strong>
                          <StarsContainer>
                            {[1, 2, 3, 4, 5].map((star) => (
                              <StarIcon
                                key={star}
                                $filled={star <= Math.round((getMyRating(entry) || 0) / 2)}
                              />
                            ))}
                          </StarsContainer>
                          <span style={{ fontWeight: 600, color: ThemeColors.primary.main }}>
                            {getMyRating(entry)}/10
                          </span>
                          {(() => {
                            const myRating = ContestEntryService.getManagerRating(entry, user?.id || '');
                            return myRating?.notes ? (
                              <div style={{ fontSize: Typography.fontSize.sm, color: ThemeColors.text.secondary, marginTop: 4 }}>
                                "{myRating.notes}"
                              </div>
                            ) : null;
                          })()}
                        </RatingDisplay>
                      )}
                      
                      {/* Show overall rating */}
                      <RatingDisplay>
                        <strong>{entry.averageRating ? 'Overall Rating:' : 'Rating:'}</strong>
                        <StarsContainer>
                          {[1, 2, 3, 4, 5].map((star) => (
                            <StarIcon
                              key={star}
                              $filled={star <= Math.round(getEffectiveRating(entry) / 2)}
                            />
                          ))}
                        </StarsContainer>
                        <span>({entry.averageRating ? entry.averageRating.toFixed(1) : entry.rating}/10)</span>
                        {entry.totalRatings && (
                          <span style={{ fontSize: Typography.fontSize.xs, color: ThemeColors.text.secondary, marginLeft: Spacing.xs }}>
                            • {entry.totalRatings} {entry.totalRatings === 1 ? 'manager' : 'managers'}
                          </span>
                        )}
                      </RatingDisplay>
                    </RatingSection>
                  )}

                  {user?.role === 'manager' && (
                    <ActionButtons>
                      {!haveIRated(entry) ? (
                        <Button
                          variant="primary"
                          onClick={() => setRatingEntry(entry)}
                        >
                          <Star size={16} />
                          Rate Entry
                        </Button>
                      ) : (
                        <Button
                          variant="secondary"
                          onClick={() => {
                            const myRating = ContestEntryService.getManagerRating(entry, user?.id || '');
                            setRatingEntry(entry);
                            setRating(myRating?.rating || 0);
                            setRatingNote(myRating?.notes || '');
                          }}
                        >
                          <Star size={16} />
                          Update Your Rating
                        </Button>
                      )}
                      <Button
                        variant="secondary"
                        onClick={() => setSelectedEntry(entry)}
                      >
                        <Eye size={16} />
                        View
                      </Button>
                    </ActionButtons>
                  )}
                </EntryContent>
              </EntryCard>
            ))}
          </EntriesGrid>
        </EntriesContainer>
      )}

      {/* Rating Modal */}
      {ratingEntry && (
        <Modal isOpen={!!ratingEntry}>
          <ModalContent>
            <ModalHeader>
              <h2>Rate Entry</h2>
              <ModalClose onClick={() => {
                setRatingEntry(null);
                setRating(0);
                setRatingNote('');
              }}>
                <X size={24} />
              </ModalClose>
            </ModalHeader>
            <div style={{ padding: Spacing.lg }}>
              <EntryImage src={ratingEntry.imageUrl} alt={ratingEntry.storeName} />
              <div style={{ marginTop: Spacing.lg }}>
                <h3>{ratingEntry.storeName}</h3>
                <p style={{ color: ThemeColors.text.secondary }}>
                  Submitted by {ratingEntry.submittedByName} on {formatDate(ratingEntry.submittedAt)}
                </p>
                {ratingEntry.message && (
                  <EntryMessage>{ratingEntry.message}</EntryMessage>
                )}
              </div>
              <RatingInput>
                <FilterLabel>Rating (1-10)</FilterLabel>
                <RatingStars
                  onMouseLeave={() => setHoveredRating(0)}
                >
                  {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((star) => (
                    <RatingStar
                      key={star}
                      $filled={star <= rating}
                      $hovered={star <= hoveredRating}
                      onClick={() => setRating(star)}
                      onMouseEnter={() => setHoveredRating(star)}
                    />
                  ))}
                </RatingStars>
                <p style={{ fontSize: Typography.fontSize.sm, color: ThemeColors.text.secondary }}>
                  Selected: {rating}/10
                </p>
                <FilterLabel>Notes (Optional)</FilterLabel>
                <RatingNote
                  value={ratingNote}
                  onChange={(e) => setRatingNote(e.target.value)}
                  placeholder="Add any notes about this entry..."
                />
                <ActionButtons style={{ marginTop: Spacing.lg }}>
                  <Button
                    variant="secondary"
                    onClick={() => {
                      setRatingEntry(null);
                      setRating(0);
                      setRatingNote('');
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="primary"
                    onClick={() => handleRating(ratingEntry, rating)}
                    disabled={rating === 0}
                  >
                    <Check size={16} />
                    Save Rating
                  </Button>
                </ActionButtons>
              </RatingInput>
            </div>
          </ModalContent>
        </Modal>
      )}

      {/* Image View Modal */}
      {selectedEntry && (
        <Modal isOpen={!!selectedEntry} onClick={() => setSelectedEntry(null)}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <h2>{selectedEntry.storeName}</h2>
              <ModalClose onClick={() => setSelectedEntry(null)}>
                <X size={24} />
              </ModalClose>
            </ModalHeader>
            <ModalImage src={selectedEntry.imageUrl} alt={selectedEntry.storeName} />
            <div style={{ padding: Spacing.lg }}>
              <EntryInfo>
                <InfoRow>
                  <MapPin size={16} />
                  {selectedEntry.storeAddress}, {selectedEntry.storeCity}
                  {selectedEntry.storeProvince && `, ${selectedEntry.storeProvince}`}
                </InfoRow>
                <InfoRow>
                  <strong>Customer #:</strong> {selectedEntry.storeCustomerNo}
                </InfoRow>
                <InfoRow>
                  <User size={16} />
                  {selectedEntry.submittedByName}
                </InfoRow>
                <InfoRow>
                  <Calendar size={16} />
                  {formatDate(selectedEntry.submittedAt)}
                </InfoRow>
              </EntryInfo>
              {selectedEntry.message && (
                <EntryMessage>{selectedEntry.message}</EntryMessage>
              )}
              {(selectedEntry.averageRating || selectedEntry.rating) && (
                <RatingSection>
                  {/* Show manager's own rating prominently if they've rated */}
                  {ContestEntryService.hasManagerRated(selectedEntry, user?.id || '') && (
                    <div style={{ 
                      marginBottom: Spacing.md, 
                      padding: Spacing.sm,
                      background: ThemeColors.primary.light + '20',
                      borderRadius: Spacing.xs,
                      borderLeft: `4px solid ${ThemeColors.primary.main}`
                    }}>
                      <RatingDisplay>
                        <strong style={{ color: ThemeColors.primary.main }}>Your Rating:</strong>
                        <StarsContainer>
                          {[1, 2, 3, 4, 5].map((star) => (
                            <StarIcon
                              key={star}
                              $filled={star <= Math.round((ContestEntryService.getManagerRating(selectedEntry, user?.id || '')?.rating || 0) / 2)}
                            />
                          ))}
                        </StarsContainer>
                        <span style={{ fontWeight: 600, color: ThemeColors.primary.main }}>
                          {ContestEntryService.getManagerRating(selectedEntry, user?.id || '')?.rating}/10
                        </span>
                      </RatingDisplay>
                      {(() => {
                        const myRating = ContestEntryService.getManagerRating(selectedEntry, user?.id || '');
                        return myRating?.notes ? (
                          <div style={{ fontSize: Typography.fontSize.sm, color: ThemeColors.text.secondary, marginTop: Spacing.xs }}>
                            Your notes: "{myRating.notes}"
                          </div>
                        ) : null;
                      })()}
                    </div>
                  )}
                  
                  {/* Show overall rating */}
                  <RatingDisplay>
                    <strong>{selectedEntry.averageRating ? 'Overall Rating:' : 'Rating:'}</strong>
                    <StarsContainer>
                      {[1, 2, 3, 4, 5].map((star) => (
                        <StarIcon
                          key={star}
                          $filled={star <= Math.round((selectedEntry.averageRating || selectedEntry.rating || 0) / 2)}
                        />
                      ))}
                    </StarsContainer>
                    <span>({selectedEntry.averageRating ? selectedEntry.averageRating.toFixed(1) : selectedEntry.rating}/10)</span>
                    {selectedEntry.totalRatings && (
                      <span style={{ fontSize: Typography.fontSize.sm, color: ThemeColors.text.secondary, marginLeft: Spacing.xs }}>
                        • {selectedEntry.totalRatings} {selectedEntry.totalRatings === 1 ? 'manager' : 'managers'}
                      </span>
                    )}
                  </RatingDisplay>
                  
                  {/* Show all individual ratings if using new system */}
                  {selectedEntry.ratings && selectedEntry.ratings.length > 1 && (
                    <div style={{ marginTop: Spacing.md }}>
                      <strong style={{ fontSize: Typography.fontSize.sm, color: ThemeColors.text.secondary }}>
                        All Manager Ratings:
                      </strong>
                      {selectedEntry.ratings.map((rating, index) => (
                        <div 
                          key={index} 
                          style={{ 
                            fontSize: Typography.fontSize.sm, 
                            color: ThemeColors.text.primary, 
                            marginTop: Spacing.xs,
                            paddingLeft: Spacing.md,
                            borderLeft: rating.managerId === user?.id ? `3px solid ${ThemeColors.primary.main}` : `1px solid ${ThemeColors.border.light}`
                          }}
                        >
                          <strong>{rating.managerName}</strong> 
                          {rating.managerId === user?.id && ' (You)'}: {rating.rating}/10
                          {rating.notes && ` - "${rating.notes}"`}
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {/* Show legacy notes if no new ratings */}
                  {!selectedEntry.ratings && selectedEntry.notes && (
                    <p style={{ fontSize: Typography.fontSize.sm, color: ThemeColors.text.secondary, marginTop: Spacing.xs }}>
                      {selectedEntry.notes}
                    </p>
                  )}
                </RatingSection>
              )}
            </div>
          </ModalContent>
        </Modal>
      )}
      </MainContent>
    </PageContainer>
  );
};

